import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency, CURRENCIES } from '../contexts/CurrencyContext';
import { useTransactions } from '../contexts/TransactionContext';
import Layout from '../components/Layout';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  LogOut, User, Sun, Moon, Monitor, Target, Calendar, RefreshCw, 
  ChevronRight, Users, Globe, ArrowRightLeft, History, AlertTriangle,
  Check, Loader2, Undo2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Settings = () => {
  const { currentUser, signOut } = useAuth();
  const { themeMode, setTheme, theme, isDark } = useTheme();
  const { 
    displayCurrency, 
    exchangeRates, 
    conversionHistory,
    ratesLoading,
    ratesError,
    lastRatesUpdate,
    getCurrencyInfo, 
    getExchangeRate,
    changeDisplayCurrency,
    convertAllTransactions,
    undoConversion,
    canUndoConversion,
    refreshRates
  } = useCurrency();
  const { allTransactions } = useTransactions();
  const navigate = useNavigate();
  
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [showCurrencyDialog, setShowCurrencyDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [selectedConversion, setSelectedConversion] = useState(null);
  
  const [newCurrency, setNewCurrency] = useState(displayCurrency);
  const [convertTransactions, setConvertTransactions] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  const quickLinks = [
    { path: '/goals', label: 'Goals', icon: Target, color: '#4CAF50', description: 'Savings goals' },
    { path: '/bills', label: 'Bills', icon: Calendar, color: '#FF9800', description: 'Bill reminders' },
    { path: '/subscriptions', label: 'Subscriptions', icon: RefreshCw, color: '#9C27B0', description: 'Track recurring' },
    { path: '/family', label: 'Family / Shared', icon: Users, color: '#5B9FE3', description: 'Shared budgets' },
  ];

  const currentCurrencyInfo = getCurrencyInfo(displayCurrency);
  const newCurrencyInfo = getCurrencyInfo(newCurrency);

  // Filter currencies based on search
  const filteredCurrencies = CURRENCIES.filter(c => 
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currencySearch.toLowerCase())
  );

  const handleChangeCurrency = async () => {
    if (newCurrency === displayCurrency) {
      setShowCurrencyDialog(false);
      return;
    }

    setIsConverting(true);
    try {
      if (convertTransactions && allTransactions.length > 0) {
        await convertAllTransactions(newCurrency, allTransactions);
        toast.success(`Currency changed to ${newCurrencyInfo.code}. ${allTransactions.length} transactions converted.`);
      } else {
        await changeDisplayCurrency(newCurrency);
        toast.success(`Display currency changed to ${newCurrencyInfo.code}`);
      }
      setShowCurrencyDialog(false);
      setConvertTransactions(false);
    } catch (error) {
      toast.error('Failed to change currency');
    } finally {
      setIsConverting(false);
    }
  };

  const handleUndoConversion = async () => {
    if (!selectedConversion) return;
    
    setIsConverting(true);
    try {
      await undoConversion(selectedConversion.id);
      toast.success(`Currency conversion undone. Restored to ${selectedConversion.fromCurrency}`);
      setShowUndoDialog(false);
      setSelectedConversion(null);
    } catch (error) {
      toast.error(error.message || 'Failed to undo conversion');
    } finally {
      setIsConverting(false);
    }
  };

  const rate = newCurrency !== displayCurrency ? getExchangeRate(displayCurrency, newCurrency) : 1;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6" style={{ color: theme.text }}>Settings</h1>

        {/* User Profile Card */}
        <div 
          className="rounded-lg p-6 mb-6" 
          style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
        >
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={currentUser?.photoURL} alt={currentUser?.displayName} />
              <AvatarFallback style={{ backgroundColor: isDark ? theme.inputBackground : '#E9ECEF' }}>
                <User className="h-8 w-8" style={{ color: theme.textSecondary }} />
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-lg font-semibold" style={{ color: theme.text }}>
                {currentUser?.displayName || 'User'}
              </div>
              <div className="text-sm" style={{ color: theme.textSecondary }}>
                {currentUser?.email}
              </div>
            </div>
          </div>
        </div>

        {/* Currency Settings - NEW SECTION */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>Currency</h2>
          <div 
            className="rounded-lg p-6" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            {/* Current Currency Display */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-sm font-medium mb-1 block" style={{ color: theme.textSecondary }}>
                  Display Currency
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{currentCurrencyInfo.flag}</span>
                  <div>
                    <p className="font-semibold" style={{ color: theme.text }}>
                      {currentCurrencyInfo.code} ({currentCurrencyInfo.symbol})
                    </p>
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      {currentCurrencyInfo.name}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => {
                  setNewCurrency(displayCurrency);
                  setShowCurrencyDialog(true);
                }}
                variant="outline"
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                <Globe className="w-4 h-4 mr-2" />
                Change
              </Button>
            </div>

            {/* Conversion History Link */}
            {conversionHistory.length > 0 && (
              <button
                onClick={() => setShowHistoryDialog(true)}
                className="w-full flex items-center justify-between p-3 rounded-lg transition-colors mb-4"
                style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
              >
                <div className="flex items-center gap-3">
                  <History className="w-5 h-5" style={{ color: theme.textSecondary }} />
                  <div className="text-left">
                    <p className="font-medium text-sm" style={{ color: theme.text }}>
                      Conversion History
                    </p>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {conversionHistory.length} conversion{conversionHistory.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: theme.textSecondary }} />
              </button>
            )}

            {/* Exchange Rate Status */}
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium" style={{ color: theme.text }}>Exchange Rates</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await refreshRates();
                      toast.success('Exchange rates refreshed');
                    } catch (e) {
                      toast.error('Failed to refresh rates');
                    }
                  }}
                  disabled={ratesLoading}
                  style={{ color: theme.accent }}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${ratesLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              {ratesError && (
                <p className="text-xs mb-2" style={{ color: theme.accent }}>
                  Using fallback rates (API error: {ratesError})
                </p>
              )}
              {lastRatesUpdate && (
                <p className="text-xs" style={{ color: theme.textTertiary }}>
                  Last updated: {format(lastRatesUpdate, 'MMM d, yyyy h:mm a')}
                </p>
              )}
              {exchangeRates && exchangeRates.USD && (
                <div className="mt-2 text-xs" style={{ color: theme.textSecondary }}>
                  <span>Sample rates: </span>
                  <span>1 USD = {exchangeRates.INR?.toFixed(2)} INR</span>
                  <span className="mx-2">•</span>
                  <span>1 USD = {exchangeRates.EUR?.toFixed(2)} EUR</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Links - Goals, Bills, Subscriptions */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>Manage</h2>
          <div 
            className="rounded-lg overflow-hidden" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="w-full flex items-center justify-between p-4 transition-colors"
                  style={{ 
                    borderBottom: index < quickLinks.length - 1 ? `1px solid ${theme.divider}` : 'none',
                    backgroundColor: 'transparent'
                  }}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${link.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: link.color }} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium" style={{ color: theme.text }}>{link.label}</p>
                      <p className="text-xs" style={{ color: theme.textSecondary }}>{link.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: theme.textSecondary }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Style Settings (Dark Mode) */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>Style</h2>
          <div 
            className="rounded-lg p-6" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            <Label className="text-sm font-medium mb-3 block" style={{ color: theme.textSecondary }}>
              Theme
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = themeMode === option.value;
                
                return (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-200"
                    style={{
                      backgroundColor: isSelected 
                        ? (isDark ? theme.inputBackground : '#FEE2E2') 
                        : (isDark ? theme.cardBackground : '#F8F9FA'),
                      borderColor: isSelected ? theme.accent : 'transparent',
                    }}
                    data-testid={`theme-${option.value}`}
                  >
                    <Icon 
                      className="w-6 h-6" 
                      style={{ color: isSelected ? theme.accent : theme.textSecondary }} 
                    />
                    <span 
                      className="text-sm font-medium"
                      style={{ color: isSelected ? theme.accent : theme.text }}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-3" style={{ color: theme.textSecondary }}>
              {themeMode === 'system' 
                ? 'Theme will follow your device settings' 
                : `Currently using ${themeMode} mode`}
            </p>
          </div>
        </div>

        {/* About */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>About</h2>
          <div 
            className="rounded-lg p-6" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            <div className="text-sm" style={{ color: theme.textSecondary }}>
              Version: 1.3.0
            </div>
          </div>
        </div>

        {/* Sign Out Button */}
        <Button
          onClick={() => setShowSignOutDialog(true)}
          className="w-full h-12"
          style={{ backgroundColor: '#DC3545', borderRadius: '8px' }}
          data-testid="sign-out-button"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>

        {/* Sign Out Confirmation Dialog */}
        <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Sign Out</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to sign out?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ borderColor: theme.divider, color: theme.text }}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleSignOut}
                style={{ backgroundColor: '#DC3545' }}
              >
                Sign Out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Change Currency Dialog */}
        <Dialog open={showCurrencyDialog} onOpenChange={setShowCurrencyDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Change Currency</DialogTitle>
              <DialogDescription style={{ color: theme.textSecondary }}>
                Select your new display currency
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Currency Search */}
              <input
                type="text"
                placeholder="Search currencies..."
                value={currencySearch}
                onChange={(e) => setCurrencySearch(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border"
                style={{ 
                  backgroundColor: isDark ? theme.inputBackground : 'white',
                  borderColor: theme.divider,
                  color: theme.text
                }}
              />

              {/* Currency List */}
              <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg" style={{ border: `1px solid ${theme.divider}` }}>
                {filteredCurrencies.map((currency) => {
                  const isSelected = currency.code === newCurrency;
                  return (
                    <button
                      key={currency.code}
                      onClick={() => setNewCurrency(currency.code)}
                      className="w-full flex items-center justify-between p-3 transition-colors"
                      style={{ 
                        backgroundColor: isSelected ? (isDark ? theme.inputBackground : '#FEE2E2') : 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{currency.flag}</span>
                        <div className="text-left">
                          <p className="font-medium text-sm" style={{ color: theme.text }}>
                            {currency.code} - {currency.name}
                          </p>
                          <p className="text-xs" style={{ color: theme.textSecondary }}>
                            {currency.symbol}
                          </p>
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5" style={{ color: theme.accent }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Exchange Rate Display */}
              {newCurrency !== displayCurrency && (
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
                >
                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                    Current Rate:
                  </p>
                  <p className="font-semibold" style={{ color: theme.text }}>
                    1 {displayCurrency} = {rate.toFixed(4)} {newCurrency}
                  </p>
                </div>
              )}

              {/* Convert Transactions Checkbox */}
              {newCurrency !== displayCurrency && allTransactions.length > 0 && (
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: isDark ? '#3D3520' : '#FEF3C7',
                    borderColor: isDark ? '#D97706' : '#FCD34D'
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="convert-transactions"
                      checked={convertTransactions}
                      onCheckedChange={setConvertTransactions}
                      className="mt-1"
                    />
                    <label htmlFor="convert-transactions" className="cursor-pointer">
                      <p className="font-medium text-sm" style={{ color: isDark ? '#FFB74D' : '#92400E' }}>
                        Convert all existing transactions to {newCurrency}?
                      </p>
                      <p className="text-xs mt-1" style={{ color: isDark ? '#FFB74D' : '#B45309' }}>
                        {convertTransactions 
                          ? `All ${allTransactions.length} transaction amounts will be converted using current exchange rates. You can undo this within 7 days.`
                          : `Amounts will remain in their original currencies. Totals will be converted for display.`
                        }
                      </p>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowCurrencyDialog(false)}
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangeCurrency}
                disabled={isConverting || newCurrency === displayCurrency}
                style={{ backgroundColor: theme.accent, color: 'white' }}
              >
                {isConverting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {convertTransactions ? 'Converting...' : 'Changing...'}
                  </>
                ) : (
                  'Change Currency'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Conversion History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-md" style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Conversion History</DialogTitle>
              <DialogDescription style={{ color: theme.textSecondary }}>
                You can undo conversions within 7 days
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 py-4 max-h-80 overflow-y-auto">
              {conversionHistory.map((conversion) => {
                const canUndo = canUndoConversion(conversion);
                const conversionDate = conversion.timestamp instanceof Date 
                  ? conversion.timestamp 
                  : new Date();
                
                return (
                  <div
                    key={conversion.id}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA', border: `1px solid ${theme.divider}` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4" style={{ color: theme.textSecondary }} />
                        <span className="font-medium" style={{ color: theme.text }}>
                          {conversion.fromCurrency} → {conversion.toCurrency}
                        </span>
                      </div>
                      {canUndo && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedConversion(conversion);
                            setShowUndoDialog(true);
                          }}
                          style={{ borderColor: theme.accent, color: theme.accent }}
                        >
                          <Undo2 className="w-3 h-3 mr-1" />
                          Undo
                        </Button>
                      )}
                    </div>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {format(conversionDate, 'MMM d, yyyy h:mm a')}
                    </p>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {conversion.transactionCount} transactions converted
                    </p>
                    {!canUndo && (
                      <p className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                        Cannot undo - more than 7 days old
                      </p>
                    )}
                  </div>
                );
              })}
              
              {conversionHistory.length === 0 && (
                <div className="text-center py-8">
                  <p style={{ color: theme.textSecondary }}>No conversion history</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button 
                onClick={() => setShowHistoryDialog(false)}
                style={{ backgroundColor: theme.accent, color: 'white' }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Undo Conversion Confirmation */}
        <AlertDialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2" style={{ color: theme.text }}>
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Undo Currency Conversion
              </AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                {selectedConversion && (
                  <>
                    This will restore all {selectedConversion.transactionCount} transactions 
                    to their original amounts in {selectedConversion.fromCurrency}.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={() => setSelectedConversion(null)}
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleUndoConversion}
                disabled={isConverting}
                style={{ backgroundColor: theme.accent }}
              >
                {isConverting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Undoing...
                  </>
                ) : (
                  'Undo Conversion'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Settings;
